// Auth service wrappers mapping legacy PHP request-style API to modern fetch usage.
// Assumptions (adjust if backend differs):
// - Single POST endpoint that switches behavior based on a `request` field.
// - JSON responses shaped as: { error?: { message: string }, message?: string, data?: any }
// - File uploads require multipart/form-data with fields described below.
// Update API_BASE via REACT_APP_API_BASE env var if needed.

export interface ApiErrorShape { message: string }
export interface ApiResponse<T=any> { error?: ApiErrorShape; message?: string; data?: T }

const API_BASE = (process.env.REACT_APP_API_BASE || '/portal/portal_api.php').replace(/\/+$/, '');

async function postJson(body: Record<string, any>): Promise<ApiResponse> {
  const res = await fetch(API_BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  return parseResponse(res);
}

async function postFormData(fd: FormData): Promise<ApiResponse> {
  const res = await fetch(API_BASE, { method: 'POST', body: fd });
  return parseResponse(res);
}

async function parseResponse(res: Response): Promise<ApiResponse> {
  let json: any = null;
  try { json = await res.json(); } catch { /* non JSON */ }
  if (!res.ok) {
    return { error: { message: json?.error?.message || json?.message || `HTTP ${res.status}` } };
  }
  return json || { message: 'No response body' };
}

export interface LoginResult { user_details?: any; [k: string]: any }
export async function login(emailOrUsername: string, password: string) {
  const body = { request: 'login', email: emailOrUsername, password };
  const resp = await postJson(body);
  if (resp.error) throw new Error(resp.error.message);
  return resp as ApiResponse<LoginResult>;
}

export interface ForgotPasswordResult { [k: string]: any }
export async function forgotPassword(email: string) {
  const body = { request: 'forgot_password', data: { email } };
  const resp = await postJson(body);
  if (resp.error) throw new Error(resp.error.message);
  return resp as ApiResponse<ForgotPasswordResult>;
}

export interface AccountRequestPayload {
  first_name: string;
  email: string;
  mobile: string;
  password: string;
  address: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  business_type: string;
  id_document_type: string;
  business_document_type?: string;
  notes?: string;
}

export interface AccountRequestResult { [k: string]: any }
export async function requestAccount(data: AccountRequestPayload, idFile: File, bizFile?: File) {
  const fd = new FormData();
  fd.append('request', 'user_account_request');
  fd.append('data', JSON.stringify(data));
  fd.append('id_document', idFile);
  if (bizFile) fd.append('business_document', bizFile);
  const resp = await postFormData(fd);
  if (resp.error) throw new Error(resp.error.message);
  return resp as ApiResponse<AccountRequestResult>;
}

// Helper persistence (mirrors legacy saveToStorage)
export function persistUser(user: any) {
  try {
    localStorage.setItem('logged_in', 'true');
    localStorage.setItem('user_details', JSON.stringify(user));
  } catch {/* ignore */}
}

export function clearUser() {
  try {
    localStorage.removeItem('logged_in');
    localStorage.removeItem('user_details');
  } catch {/* ignore */}
}
